import React, { useState, useMemo } from 'react';
import { 
  Clock, 
  FileText, 
  Upload, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  User, 
  Truck, 
  Shield, 
  Settings,
  Calendar,
  Filter,
  MoreVertical
} from 'lucide-react';
import { format, parseISO, isToday, isYesterday, differenceInDays } from 'date-fns';

const RecentActivityFeed = ({ 
  activities = [], 
  className = "",
  maxItems = 15,
  showFilters = true,
  autoRefresh = false,
  useSampleData = true 
}) => {
  const [filterType, setFilterType] = useState('all');
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Sample activity data if none provided
  const defaultActivities = [
    {
      id: '1',
      type: 'document_upload',
      title: 'CDL License uploaded',
      description: 'John Smith uploaded renewed CDL license',
      entityName: 'John Smith',
      entityType: 'driver',
      timestamp: '2024-12-09T10:30:00Z',
      status: 'success',
      documentType: 'license',
      metadata: { documentNumber: 'CDL-123456789', expiryDate: '2025-12-15' }
    },
    {
      id: '2',
      type: 'document_expiry_warning',
      title: 'Vehicle registration expiring soon',
      description: 'TR-001 vehicle registration expires in 7 days',
      entityName: 'Truck TR-001',
      entityType: 'vehicle',
      timestamp: '2024-12-09T09:15:00Z',
      status: 'warning',
      documentType: 'registration',
      metadata: { expiryDate: '2024-12-16', daysRemaining: 7 }
    },
    {
      id: '3',
      type: 'system_change',
      title: 'Driver status updated',
      description: 'Sarah Johnson marked as available for routes',
      entityName: 'Sarah Johnson',
      entityType: 'driver',
      timestamp: '2024-12-09T08:45:00Z',
      status: 'info',
      metadata: { previousStatus: 'off-duty', newStatus: 'available' }
    },
    {
      id: '4',
      type: 'document_expired',
      title: 'DOT Medical Certificate expired',
      description: 'Mike Wilson DOT medical certificate has expired',
      entityName: 'Mike Wilson',
      entityType: 'driver',
      timestamp: '2024-12-09T07:00:00Z',
      status: 'error',
      documentType: 'medical',
      metadata: { expiredDate: '2024-12-09' }
    },
    {
      id: '5',
      type: 'vehicle_maintenance',
      title: 'Maintenance scheduled',
      description: 'TR-002 scheduled for routine maintenance',
      entityName: 'Truck TR-002',
      entityType: 'vehicle',
      timestamp: '2024-12-08T16:20:00Z',
      status: 'info',
      metadata: { maintenanceType: 'routine', scheduledDate: '2024-12-12' }
    },
    {
      id: '6',
      type: 'document_upload',
      title: 'Insurance policy updated',
      description: 'Fleet insurance policy documents updated',
      entityName: 'Fleet Coverage',
      entityType: 'company',
      timestamp: '2024-12-08T14:30:00Z',
      status: 'success',
      documentType: 'insurance',
      metadata: { policyNumber: 'INS-789123456' }
    },
    {
      id: '7',
      type: 'compliance_check',
      title: 'Compliance audit completed',
      description: 'Monthly compliance check completed with 94% score',
      entityName: 'System',
      entityType: 'system',
      timestamp: '2024-12-08T12:00:00Z',
      status: 'success',
      metadata: { complianceScore: 94, previousScore: 91 }
    },
    {
      id: '8',
      type: 'document_expiry_warning',
      title: 'Hazmat endorsement expiring',
      description: 'Driver endorsement expires in 14 days',
      entityName: 'Mike Wilson',
      entityType: 'driver',
      timestamp: '2024-12-08T10:15:00Z',
      status: 'warning',
      documentType: 'endorsement',
      metadata: { expiryDate: '2024-12-22', daysRemaining: 14 }
    }
  ];

  const hasProvidedActivities = Array.isArray(activities);
  const hasActivityData = hasProvidedActivities && activities.length > 0;
  const activityData = hasActivityData
    ? activities
    : useSampleData
      ? defaultActivities
      : [];

  // Process and sort activities
  const processedActivities = useMemo(() => {
    return activityData
      .map(activity => ({
        ...activity,
        timestamp: parseISO(activity.timestamp),
        formattedTime: format(parseISO(activity.timestamp), 'HH:mm'),
        formattedDate: format(parseISO(activity.timestamp), 'MMM dd, yyyy'),
        isToday: isToday(parseISO(activity.timestamp)),
        isYesterday: isYesterday(parseISO(activity.timestamp)),
        daysAgo: differenceInDays(new Date(), parseISO(activity.timestamp))
      }))
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [activityData]);

  // Filter activities
  const filteredActivities = useMemo(() => {
    if (filterType === 'all') return processedActivities;
    return processedActivities.filter(activity => activity.type === filterType);
  }, [processedActivities, filterType]);

  const displayedActivities = filteredActivities.slice(0, maxItems);

  // Activity type configuration
  const activityConfig = {
    document_upload: {
      icon: Upload,
      color: 'success',
      bgColor: 'bg-success-100',
      iconColor: 'text-success-600',
      label: 'Document Upload'
    },
    document_expiry_warning: {
      icon: AlertTriangle,
      color: 'warning',
      bgColor: 'bg-warning-100',
      iconColor: 'text-warning-600',
      label: 'Expiry Warning'
    },
    document_expired: {
      icon: XCircle,
      color: 'danger',
      bgColor: 'bg-danger-100',
      iconColor: 'text-danger-600',
      label: 'Document Expired'
    },
    system_change: {
      icon: Settings,
      color: 'primary',
      bgColor: 'bg-primary-100',
      iconColor: 'text-primary-600',
      label: 'System Change'
    },
    vehicle_maintenance: {
      icon: Truck,
      color: 'slate',
      bgColor: 'bg-slate-100',
      iconColor: 'text-slate-600',
      label: 'Vehicle Maintenance'
    },
    compliance_check: {
      icon: Shield,
      color: 'success',
      bgColor: 'bg-success-100',
      iconColor: 'text-success-600',
      label: 'Compliance Check'
    }
  };

  // Status colors
  const getStatusColor = (status) => {
    const statusMap = {
      success: 'text-success-600',
      warning: 'text-warning-600',
      error: 'text-danger-600',
      info: 'text-primary-600'
    };
    return statusMap[status] || 'text-slate-600';
  };

  // Get entity icon
  const getEntityIcon = (entityType) => {
    const iconMap = {
      driver: User,
      vehicle: Truck,
      company: Shield,
      system: Settings
    };
    return iconMap[entityType] || FileText;
  };

  // Get relative time display
  const getRelativeTime = (activity) => {
    if (activity.isToday) return `Today, ${activity.formattedTime}`;
    if (activity.isYesterday) return `Yesterday, ${activity.formattedTime}`;
    if (activity.daysAgo <= 7) return `${activity.daysAgo} days ago`;
    return activity.formattedDate;
  };

  // Toggle expanded view
  const toggleExpanded = (activityId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(activityId)) {
        newSet.delete(activityId);
      } else {
        newSet.add(activityId);
      }
      return newSet;
    });
  };

  // Filter options
  const filterOptions = [
    { value: 'all', label: 'All Activities', count: processedActivities.length },
    { value: 'document_upload', label: 'Uploads', count: processedActivities.filter(a => a.type === 'document_upload').length },
    { value: 'document_expiry_warning', label: 'Expiring', count: processedActivities.filter(a => a.type === 'document_expiry_warning').length },
    { value: 'document_expired', label: 'Expired', count: processedActivities.filter(a => a.type === 'document_expired').length },
    { value: 'system_change', label: 'System', count: processedActivities.filter(a => a.type === 'system_change').length }
  ];

  return (
    <div className={`bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200 ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Clock className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Recent Activity</h3>
              <p className="text-sm text-slate-600">Latest system updates and changes</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-slate-900">{processedActivities.length}</span>
            <span className="text-sm text-slate-600">items</span>
          </div>
        </div>

        {/* Filter Tabs */}
        {showFilters && (
          <div className="flex flex-wrap items-center gap-1 bg-slate-100 rounded-lg p-1">
            {filterOptions.map(option => (
              <button
                key={option.value}
                onClick={() => setFilterType(option.value)}
                className={`flex items-center space-x-1 px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 ${
                  filterType === option.value
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <span className="hidden sm:inline">{option.label}</span>
                <span className="sm:hidden">{option.label.split(' ')[0]}</span>
                {option.count > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                    filterType === option.value
                      ? 'bg-slate-100 text-slate-700'
                      : 'bg-slate-200 text-slate-600'
                  }`}>
                    {option.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Activity Timeline */}
      <div className="max-h-96 overflow-y-auto">
        {displayedActivities.length === 0 ? (
          <div className="p-6 text-center">
            <Clock className="h-12 w-12 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600">No recent activities to display</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-slate-200"></div>
            
            {displayedActivities.map((activity, index) => {
              const config = activityConfig[activity.type] || activityConfig.system_change;
              const ActivityIcon = config.icon;
              const EntityIcon = getEntityIcon(activity.entityType);
              const isExpanded = expandedItems.has(activity.id);
              
              return (
                <div key={activity.id} className="relative hover:bg-slate-50 transition-colors duration-200">
                  {/* Timeline dot */}
                  <div className={`absolute left-6 w-4 h-4 rounded-full ${config.bgColor} border-2 border-white flex items-center justify-center`}>
                    <div className={`w-2 h-2 rounded-full bg-${config.color}-500`}></div>
                  </div>
                  
                  <div className="pl-16 pr-6 py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        {/* Activity header */}
                        <div className="flex items-center space-x-2 mb-1">
                          <ActivityIcon className={`h-4 w-4 ${config.iconColor}`} />
                          <h4 className="text-sm font-medium text-slate-900 truncate">
                            {activity.title}
                          </h4>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-700`}>
                            {config.label}
                          </span>
                        </div>
                        
                        {/* Activity description */}
                        <p className="text-sm text-slate-600 mb-2">
                          {activity.description}
                        </p>
                        
                        {/* Entity and time info */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-xs text-slate-500 space-y-1 sm:space-y-0">
                          <div className="flex items-center space-x-1">
                            <EntityIcon className="h-3 w-3" />
                            <span>{activity.entityName}</span>
                          </div>
                          <span className="hidden sm:inline">•</span>
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>{getRelativeTime(activity)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center space-x-1 ml-3">
                        <button
                          onClick={() => toggleExpanded(activity.id)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors duration-200"
                        >
                          <MoreVertical className={`h-4 w-4 transition-transform duration-200 ${
                            isExpanded ? 'rotate-90' : ''
                          }`} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Expanded details */}
                    {isExpanded && activity.metadata && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          {Object.entries(activity.metadata).map(([key, value]) => (
                            <div key={key}>
                              <span className="font-medium text-slate-700 capitalize">
                                {key.replace(/([A-Z])/g, ' $1').toLowerCase()}:
                              </span>
                              <p className="text-slate-600">{value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {filteredActivities.length > maxItems && (
        <div className="p-4 border-t border-slate-200 text-center">
          <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
            View all {filteredActivities.length} activities
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentActivityFeed;
